import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CrystallisationOption } from "../../../../../../../Wrap/Constants/Enums/Drawdown/CrystallisationOption";
import { CrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/CrystallisationDetails";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { SectionHeader } from "../../SharedComponents";
import {
    calculateUnCrystallizationAmount,
    calculateAvailableToCrystallise,
    hasProtectedRetireAgeOrSchemeTaxFreeCash,
    calculateAvailableCash,
} from "./Helper/DrawdownDetailHelper";
import {
    CrystallisationOptions,
    drawdownDetailsMessage,
    crystallisationAmountErrorMessage,
    CrystallisationOptionsMap,
} from "./Resource/Contents";

interface ICrystallisationDetailsProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: PensionProtectionDetails;
    processType: ProcessType;
    modelData: PensionBase;
    cashInstrmentProductId: number;
    doCalculation: VoidFunction;
    isImmediateTaxFreeLumpSum: boolean;
}

interface ICrystallisationDetailsState {}

export class CrystallisationDetailsSection extends ProcessComponent<
    ICrystallisationDetailsProps,
    ICrystallisationDetailsState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        super.componentDidMount();
        const {modelData, processType, isImmediateTaxFreeLumpSum} = this.props
        this.onCrystallisationDetailChange(
            {
                CrystallisationAmount: {
                    Amount: calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum),
                    Currency: CurrencyCode.GBP,
                },
            }
        )
    }

    private onCrystallisationDetailChange(changes) {
        this.onDataChange(this.props.onChange, changes, this.props.data, "CrystallisationDetails");
    }

    private getContributions(): number {
        const { modelData } = this.props;

        //get client single payments
        let clientTaxRelief = modelData.ClientSinglePayment?.TaxRelief || false;
        let paymentIn = (modelData.ClientSinglePayment?.Money?.Amount || 0) * (clientTaxRelief ? 1.25 : 1);

        //add employer single payments (there is no tax relief for employer payments)
        paymentIn += modelData.EmployerSinglePayment?.Money?.Amount || 0;
        paymentIn += modelData.EmployerSalaryDeductionSinglePayment?.Money?.Amount || 0;

        //add third party single payments
        let thirdPartyTaxRelief = modelData.ThirdPartySinglePayment?.TaxRelief || false;
        paymentIn += (modelData.ThirdPartySinglePayment?.Money?.Amount || 0) * (thirdPartyTaxRelief ? 1.25 : 1);

        //add transfers in
        paymentIn += modelData.PensionTransfers?.reduce((sum, ele) => ele.CurrentValue.Amount + sum, 0) || 0;

        return paymentIn;
    }

    private getConfigTable = (CrystallisationDetails: CrystallisationDetails) => {
        const { modelData, processType, isImmediateTaxFreeLumpSum } = this.props;
        let config = [
            {
                title: "Savings Value",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: modelData
                        ? calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                        : CrystallisationDetails?.UnCrystallisedValue?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Value available to move to Drawdown",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: modelData
                        ? calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum)
                        : CrystallisationDetails?.AvailableToCrystallise?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: modelData
                        ? calculateAvailableCash(modelData, processType, isImmediateTaxFreeLumpSum)
                        : CrystallisationDetails?.AvailableCash?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
        ];
        if (![ProcessType.NB, ProcessType.Quote].includes(this.props.processType)) {
            config = [
                {
                    title: "Drawdown Value",
                    data: MoneyDisplayHelper.getMoneyFormat(CrystallisationDetails?.CrystallisedValue),
                },
            ].concat(config);
        }
        return config;
    };

    private renderCrystallisationDetails(): JSX.Element {
        const { data, pensionProtection, isImmediateTaxFreeLumpSum, modelData, processType } = this.props;

        const { CrystallisationDetails } = data;
        const isUFPLS = data?.PensionOption == PensionOption.UFPLS;
        const fullValue = (processType == ProcessType.TopUpWithITFLS || isImmediateTaxFreeLumpSum
            ? {
                    Amount: calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum),
                    Currency: CrystallisationDetails.AvailableToCrystallise.Currency
            }
            : CrystallisationDetails.AvailableToCrystallise
        );

        const contributions = this.getContributions();

        return (
            <>
                <Typography type="common" wrapCss="dt-bold" config={this.getConfigTable(CrystallisationDetails)} />
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label="Drawdown options"
                    options={isImmediateTaxFreeLumpSum ? [CrystallisationOption.Full] : CrystallisationOptions}
                    getText={(v) => {
                        return CrystallisationOptionsMap[v];
                    }}
                    selected={isImmediateTaxFreeLumpSum ? CrystallisationOption.Full : CrystallisationDetails?.CrystallisationOption}
                    onChange={(v) => {
                        this.onCrystallisationDetailChange({
                            CrystallisationOption: v,
                            CrystallisationAmount: {
                                Amount:
                                    CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Full && !isImmediateTaxFreeLumpSum
                                        ? fullValue
                                        : contributions,
                                Currency: CurrencyCode.GBP,
                            },
                        });
                    }}
                    onBlur={(v) => this.props.doCalculation()}
                    alwaysValidateOnUpdate
                    validationRules={[
                        {
                            errorMessage: drawdownDetailsMessage.crystallisationOptionErrorMessage,
                            validator: (v) => {
                                return v == CrystallisationOption.Full
                                    ? true
                                    : v == CrystallisationOption.Partial &&
                                          !hasProtectedRetireAgeOrSchemeTaxFreeCash(pensionProtection);
                            },
                        },
                        {
                            errorMessage: drawdownDetailsMessage.crystallisationOptionFullErrorMessage,
                            validator: (v) => {
                                return v == CrystallisationOption.Partial
                                    ? true
                                    : v == CrystallisationOption.Full &&
                                          this.validateFullOption(
                                              isUFPLS,
                                              CrystallisationDetails,
                                              modelData,
                                              processType,
                                              isImmediateTaxFreeLumpSum
                                          );
                            },
                        },
                    ]}
                />
                <MoneyTextBoxEditor
                    cssWrapper="control-size-245"
                    label="Drawdown amount"
                    currency={CurrencyCode.GBP}
                    allowDecimals={true}
                    value={
                        CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Full || isImmediateTaxFreeLumpSum
                            ? Number(fullValue.Amount.toFixed(2))
                            : Number(contributions.toFixed(2))
                    }
                    disabled={
                        isImmediateTaxFreeLumpSum ||
                        CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Full
                    }
                    required={"Please enter"}
                    onBlur={(v) => this.props.doCalculation()}
                    onChange={(v) =>
                        this.onCrystallisationDetailChange({
                            CrystallisationAmount: { Amount: v ? Number(v.toFixed(2)) : 0, Currency: CurrencyCode.GBP },
                        })
                    }
                    validationRules={[
                        {
                            errorMessage: crystallisationAmountErrorMessage.valueToCrystalliseIsZero,
                            validator: (v) =>
                                CrystallisationDetails?.AvailableToCrystallise?.Amount != 0 ||
                                calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum) != 0,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputIsZeroAndLessThanUncrystallised,
                            validator: (v) =>
                                v == 0 &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount <
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputIsZeroAndEqualUncrystallised,
                            validator: (v) =>
                                v == 0 &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount ==
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputEqualValueToCrystallise,
                            validator: (v) =>
                                v == CrystallisationDetails?.AvailableToCrystallise?.Amount &&
                                CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Partial &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount ==
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndLessThanUncrystallised,
                            validator: (v) =>
                                v > CrystallisationDetails?.AvailableToCrystallise?.Amount &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount <
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndEqualUncrystallised,
                            validator: (v) =>
                                v > (modelData? calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum)
                            : CrystallisationDetails?.AvailableToCrystallise?.Amount) &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount ==
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndLessThanUncrystallised,
                            validator: (v) =>
                                v >
                                    Math.min(
                                        CrystallisationDetails?.AvailableToCrystallise?.Amount,
                                        CrystallisationDetails?.AvailableCash.Amount
                                    ) &&
                                data.PensionOption &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount <
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndEqualUncrystallised,
                            validator: (v) =>
                                v >
                                (isUFPLS &&
                                CrystallisationDetails?.AvailableCash?.Amount <
                                    (modelData
                                        ? calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum)
                                        : CrystallisationDetails?.AvailableToCrystallise?.Amount)
                                    ? CrystallisationDetails?.AvailableCash?.Amount
                                    : (modelData
                                        ? calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum)
                                        : CrystallisationDetails?.AvailableToCrystallise?.Amount))
                                    ? false
                                    : true,
                        },
                    ]}
                />
            </>
        );
    }

    private validateFullOption(
        isUFPLS: boolean,
        data: CrystallisationDetails,
        modelData: PensionBase,
        processType: ProcessType,
        isImmediateTaxFreeLumpSum: boolean
    ): boolean {
        return (
            isImmediateTaxFreeLumpSum ||
            (data?.AvailableToCrystallise?.Amount ==
                calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum) &&
                calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum) ==
                    data?.AvailableCash?.Amount)
        );
    }

    public render(): JSX.Element {
        return (
            <>
                {SectionHeader("Drawdown details")}
                {this.renderCrystallisationDetails()}
            </>
        );
    }
}
